/**
 * Filename: AdminOperationsWorkspaces.tsx
 * Description: Live billing, safety, privacy, support, audit, content, and infrastructure admin views.
 * Created by: Teddy Gil "Shin" Ravis Orbeta
 * Created: 2026-08-27
 * Updated: 2026-08-28
 */
import React, { useCallback, useEffect, useState } from 'react';

type AdminApi = (path: string, init?: RequestInit) => Promise<unknown>;

interface BillingEvent {
  eventId: string;
  processedAt?: string;
  provider?: string;
  eventType?: string;
  accountId?: string;
  status?: string;
}

interface BillingOperationsData {
  webhookHealth?: { status?: string };
  integrations?: Record<string, { status?: string }>;
  events?: BillingEvent[];
}

interface SafetyIncident {
  eventId: string;
  occurredAt?: string;
  category?: string;
  severity?: string;
  surface?: string;
  reviewStatus?: string;
  assignedAdminId?: string;
  contentEncryption?: string;
  retentionPolicy?: string;
}

interface QueueState {
  status?: string;
  visible?: number;
  inFlight?: number;
}

interface DeletionRequest {
  accountId: string;
  requestedAt?: string;
  email?: string;
  status?: string;
  attempts?: number;
}

interface PrivacyOperationsData {
  queues?: Record<string, QueueState>;
  deletionRequests?: DeletionRequest[];
  retention?: Record<string, unknown>;
}

interface SystemOperationsData {
  queues?: Record<string, QueueState>;
  alarms?: {
    status?: string;
    items?: Array<{ alarmName: string; state?: string; updatedAt?: string; reason?: string }>;
  };
  backups?: Array<{ tableName?: string; status?: string }>;
  contentPipeline?: {
    status?: string;
    executions?: Array<{
      executionArn: string;
      name?: string;
      status?: string;
      startDate?: string;
      stopDate?: string;
    }>;
  };
}

interface AuditEvent {
  eventId: string;
  timestamp?: string;
  adminEmail?: string;
  ipAddress?: string;
  action?: string;
  targetUserId?: string;
  reason?: string;
  requestId?: string;
}

interface SupportCase {
  caseId: string;
  targetUserId?: string;
  updatedAt?: string;
  subject?: string;
  note?: string;
  priority?: string;
  status: string;
  resolution?: string;
  assignedAdminEmail?: string;
  assignedAdminId?: string;
}

interface ReadingPassage {
  passageId: string;
  title?: string;
  level?: string;
  copyrightBasis?: string;
  licenseReference?: string;
  status?: string;
  safetyReviewedAt?: string;
}

interface Clarification {
  id: string;
  type: string;
  question?: string;
  questionText?: string;
  status?: string;
}

interface GenerationJob {
  jobId: string;
  createdAt?: string;
  type?: string;
  status?: string;
  resultCounts?: Record<string, unknown>;
}

interface ContentOperationsData {
  readingPassages?: ReadingPassage[];
  clarifications?: Clarification[];
  generationJobs?: GenerationJob[];
  examContent?: { status?: string };
  contentManifest?: {
    schemaVersion: number;
    generatedAt: string;
    namespaces: Record<
      string,
      { version: number; updatedAt: string | null; updatedBy: string | null }
    >;
  };
}

const asDate = (value: unknown) =>
  value ? new Date(String(value)).toLocaleString() : 'Not recorded';
const label = (value: unknown) => String(value ?? 'unknown').replace(/_/g, ' ');

function LoadState({ loading, error }: { loading: boolean; error: string }) {
  if (loading) return <div className="workspace-empty">Loading live data...</div>;
  if (error) return <div className="alert error">{error}</div>;
  return null;
}

export function BillingOperationsPanel({ api }: { api: AdminApi }) {
  const [data, setData] = useState<BillingOperationsData>();
  const [error, setError] = useState('');
  useEffect(() => {
    api('/admin/billing-operations')
      .then((result) => setData(result as BillingOperationsData))
      .catch((value) =>
        setError(value instanceof Error ? value.message : 'Unable to load billing operations.')
      );
  }, [api]);
  if (error) return <div className="alert error">{error}</div>;
  if (!data) return null;
  return (
    <section className="panel">
      <div className="panel-heading">
        <div>
          <p className="eyebrow">Provider operations</p>
          <h2>Billing integrations and webhooks</h2>
        </div>
        <span className="status-pill">{label(data.webhookHealth?.status)}</span>
      </div>
      <div className="metric-grid metric-grid-four">
        {Object.entries(data.integrations ?? {}).map(([provider, state]) => (
          <div className="metric" key={provider}>
            <span>{provider}</span>
            <strong>{label(state.status)}</strong>
          </div>
        ))}
      </div>
      <div className="data-table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Processed</th>
              <th>Provider</th>
              <th>Event</th>
              <th>Account</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {(data.events ?? []).map((event) => (
              <tr key={event.eventId}>
                <td>{asDate(event.processedAt)}</td>
                <td>{label(event.provider)}</td>
                <td>{event.eventType}</td>
                <td>
                  <code>{event.accountId}</code>
                </td>
                <td>{label(event.status)}</td>
              </tr>
            ))}
            {!data.events?.length && (
              <tr>
                <td colSpan={5}>No provider webhook events have been processed.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function SafetyWorkspace({
  api,
  canDecrypt,
  currentAdminId,
  isPrimary,
}: {
  api: AdminApi;
  canDecrypt: boolean;
  currentAdminId: string;
  isPrimary: boolean;
}) {
  const [incidents, setIncidents] = useState<SafetyIncident[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reviewContent, setReviewContent] = useState<{ eventId: string; content: string }>();
  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = (await api('/admin/safety-incidents')) as { incidents?: SafetyIncident[] };
      setIncidents(result.incidents ?? []);
    } catch (value) {
      setError(value instanceof Error ? value.message : 'Unable to load safety incidents.');
    } finally {
      setLoading(false);
    }
  }, [api]);
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    if (!reviewContent) return;
    const timeout = window.setTimeout(() => setReviewContent(undefined), 60_000);
    return () => window.clearTimeout(timeout);
  }, [reviewContent]);
  const act = async (eventId: string, action: string) => {
    const reason = window.prompt(`Reason for ${action}:`, '')?.trim();
    if (!reason) return;
    const assignedAdminId =
      action === 'assign' ? window.prompt('Delegated administrator ID:', '')?.trim() : undefined;
    if (action === 'assign' && !assignedAdminId) return;
    try {
      setError('');
      await api('/admin/safety-incidents/action', {
        method: 'PATCH',
        body: JSON.stringify({ eventId, action, reason, assignedAdminId }),
      });
      await load();
    } catch (value) {
      setError(value instanceof Error ? value.message : 'Unable to update the safety incident.');
    }
  };
  const decrypt = async (eventId: string) => {
    const reason = window.prompt('Reason for restricted critical-content review:', '')?.trim();
    if (!reason) return;
    try {
      setError('');
      const result = (await api(`/admin/safety-incidents/${encodeURIComponent(eventId)}/decrypt`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      })) as { content: string };
      setReviewContent({ eventId, content: result.content });
    } catch (value) {
      setError(
        value instanceof Error
          ? value.message
          : 'Unable to decrypt the restricted incident content.'
      );
    }
  };
  return (
    <div className="workspace">
      <LoadState loading={loading} error={error} />
      {reviewContent && (
        <section className="panel">
          <div className="panel-heading">
            <h2>Restricted review content</h2>
            <button className="secondary" onClick={() => setReviewContent(undefined)}>
              Close
            </button>
          </div>
          <pre className="review-note">{reviewContent.content}</pre>
        </section>
      )}
      <section className="panel">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">Child safety</p>
            <h2>Moderation incidents</h2>
          </div>
          <button className="secondary" onClick={() => void load()}>
            Refresh
          </button>
        </div>
        <div className="data-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Occurred</th>
                <th>Category</th>
                <th>Severity</th>
                <th>Surface</th>
                <th>Status</th>
                <th>Assignment</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {incidents.map((incident) => (
                <tr key={incident.eventId}>
                  <td>{asDate(incident.occurredAt)}</td>
                  <td>{label(incident.category)}</td>
                  <td>{label(incident.severity)}</td>
                  <td>{incident.surface}</td>
                  <td>{label(incident.reviewStatus)}</td>
                  <td>{incident.assignedAdminId || 'Unassigned'}</td>
                  <td>
                    <div className="card-actions">
                      <button
                        className="secondary"
                        disabled={incident.reviewStatus === 'resolved' || incident.reviewStatus === 'not_required'}
                        onClick={() => void act(incident.eventId, 'assign')}
                      >
                        Assign
                      </button>
                      <button
                        className="secondary"
                        disabled={incident.reviewStatus !== 'pending' || !incident.assignedAdminId}
                        onClick={() => void act(incident.eventId, 'acknowledge')}
                      >
                        Acknowledge
                      </button>
                      <button
                        className="secondary"
                        disabled={incident.reviewStatus !== 'acknowledged'}
                        onClick={() => void act(incident.eventId, 'investigate')}
                      >
                        Investigate
                      </button>
                      <button disabled={!['investigating', 'escalated'].includes(incident.reviewStatus ?? '')} onClick={() => void act(incident.eventId, 'resolve')}>Resolve</button>
                      <button
                        className="danger"
                        disabled={!['acknowledged', 'investigating'].includes(incident.reviewStatus ?? '')}
                        onClick={() => void act(incident.eventId, 'escalate')}
                      >
                        Escalate
                      </button>
                      {canDecrypt && incident.contentEncryption === 'aws_kms' && (
                        <button
                          className="danger"
                          disabled={
                            !['acknowledged', 'investigating', 'escalated'].includes(incident.reviewStatus ?? '')
                            || (!isPrimary && incident.assignedAdminId !== currentAdminId)
                          }
                          onClick={() => void decrypt(incident.eventId)}
                        >
                          Review content
                        </button>
                      )}
                      {isPrimary && incident.severity === 'critical' && incident.retentionPolicy !== 'critical_legal_hold' && (
                        <button className="danger" onClick={() => void act(incident.eventId, 'place_hold')}>
                          Place legal hold
                        </button>
                      )}
                      {isPrimary && incident.retentionPolicy === 'critical_legal_hold' && (
                        <button className="secondary" onClick={() => void act(incident.eventId, 'release_hold')}>
                          Release legal hold
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {!incidents.length && !loading && (
                <tr>
                  <td colSpan={7}>No persisted safety incidents.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

export function PrivacyWorkspace({ api }: { api: AdminApi }) {
  const [data, setData] = useState<PrivacyOperationsData>();
  const [error, setError] = useState('');
  useEffect(() => {
    api('/admin/privacy-operations')
      .then((result) => setData(result as PrivacyOperationsData))
      .catch((value) =>
        setError(value instanceof Error ? value.message : 'Unable to load privacy operations.')
      );
  }, [api]);
  if (!data)
    return (
      <div className="workspace">
        <LoadState loading={!error} error={error} />
      </div>
    );
  return (
    <div className="workspace">
      <section className="metric-grid metric-grid-four">
        {Object.entries(data.queues ?? {}).map(([name, queue]) => (
          <div className="metric" key={name}>
            <span>{label(name)}</span>
            <strong>{queue.visible ?? label(queue.status)}</strong>
            <small>Visible messages</small>
          </div>
        ))}
      </section>
      <section className="panel">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">Data rights</p>
            <h2>Deletion requests</h2>
          </div>
        </div>
        <div className="data-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Requested</th>
                <th>Account</th>
                <th>Email</th>
                <th>Status</th>
                <th>Attempts</th>
              </tr>
            </thead>
            <tbody>
              {(data.deletionRequests ?? []).map((request) => (
                <tr key={request.accountId}>
                  <td>{asDate(request.requestedAt)}</td>
                  <td>
                    <code>{request.accountId}</code>
                  </td>
                  <td>{request.email}</td>
                  <td>{label(request.status)}</td>
                  <td>{request.attempts ?? 0}</td>
                </tr>
              ))}
              {!data.deletionRequests?.length && (
                <tr>
                  <td colSpan={5}>No deletion requests.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
      <section className="panel">
        <p className="eyebrow">Enforced retention</p>
        <dl>
          {Object.entries(data.retention ?? {}).map(([name, value]) => (
            <div key={name}>
              <dt>{label(name)}</dt>
              <dd>{label(value)}</dd>
            </div>
          ))}
        </dl>
      </section>
    </div>
  );
}

export function SystemOperationsWorkspace({ api }: { api: AdminApi }) {
  const [data, setData] = useState<SystemOperationsData>();
  const [error, setError] = useState('');
  useEffect(() => {
    api('/admin/operations')
      .then((result) => setData(result as SystemOperationsData))
      .catch((value) =>
        setError(value instanceof Error ? value.message : 'Unable to load system operations.')
      );
  }, [api]);
  if (!data)
    return (
      <div className="workspace">
        <LoadState loading={!error} error={error} />
      </div>
    );
  return (
    <div className="workspace">
      <section className="metric-grid metric-grid-four">
        {Object.entries(data.queues ?? {}).map(([name, queue]) => (
          <div className="metric" key={name}>
            <span>{label(name)}</span>
            <strong>{queue.visible ?? label(queue.status)}</strong>
            <small>{queue.inFlight ?? 0} in flight</small>
          </div>
        ))}
      </section>
      <section className="panel">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">CloudWatch</p>
            <h2>Alarms</h2>
          </div>
          <span className="status-pill">{label(data.alarms?.status)}</span>
        </div>
        <div className="data-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Alarm</th>
                <th>State</th>
                <th>Updated</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {(data.alarms?.items ?? []).map((alarm) => (
                <tr key={alarm.alarmName}>
                  <td>{alarm.alarmName}</td>
                  <td>{alarm.state}</td>
                  <td>{asDate(alarm.updatedAt)}</td>
                  <td>{alarm.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <section className="panel">
        <div className="panel-heading">
          <h2>Point-in-time recovery</h2>
        </div>
        <div className="data-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Table</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {(data.backups ?? []).map((backup) => (
                <tr key={backup.tableName}>
                  <td>
                    <code>{backup.tableName}</code>
                  </td>
                  <td>{label(backup.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <section className="panel">
        <div className="panel-heading">
          <h2>Content pipeline executions</h2>
          <span className="status-pill">{label(data.contentPipeline?.status)}</span>
        </div>
        <div className="data-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th>Started</th>
                <th>Stopped</th>
              </tr>
            </thead>
            <tbody>
              {(data.contentPipeline?.executions ?? []).map((execution) => (
                <tr key={execution.executionArn}>
                  <td>{execution.name}</td>
                  <td>{label(execution.status)}</td>
                  <td>{asDate(execution.startDate)}</td>
                  <td>{asDate(execution.stopDate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

export function AuditWorkspace({ api }: { api: AdminApi }) {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [cursor, setCursor] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const load = useCallback(
    async (next = '') => {
      setLoading(true);
      setError('');
      try {
        const result = (await api(
          `/admin/audit-events${next ? `?cursor=${encodeURIComponent(next)}` : ''}`
        )) as { events?: AuditEvent[]; cursor?: string };
        setEvents(result.events ?? []);
        setCursor(result.cursor ?? '');
      } catch (value) {
        setError(value instanceof Error ? value.message : 'Unable to load audit events.');
      } finally {
        setLoading(false);
      }
    },
    [api]
  );
  useEffect(() => {
    void load();
  }, [load]);
  return (
    <div className="workspace">
      <LoadState loading={loading} error={error} />
      <section className="panel">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">Append-only evidence</p>
            <h2>Administrator audit events</h2>
          </div>
          <button className="secondary" onClick={() => void load()}>
            Refresh
          </button>
        </div>
        <div className="data-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Administrator</th>
                <th>Action</th>
                <th>Target</th>
                <th>Reason</th>
                <th>Request</th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <tr key={event.eventId}>
                  <td>{asDate(event.timestamp)}</td>
                  <td>
                    {event.adminEmail}
                    <small>{event.ipAddress}</small>
                  </td>
                  <td>{label(event.action)}</td>
                  <td>
                    <code>{event.targetUserId}</code>
                  </td>
                  <td>{event.reason}</td>
                  <td>
                    <code>{event.requestId}</code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {cursor && (
          <button className="secondary" onClick={() => void load(cursor)}>
            Load older events
          </button>
        )}
      </section>
    </div>
  );
}

export function SupportWorkspace({ api, writable }: { api: AdminApi; writable: boolean }) {
  const [cases, setCases] = useState<SupportCase[]>([]);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState({
    targetUserId: '',
    subject: '',
    note: '',
    priority: 'normal',
  });
  const load = useCallback(async () => {
    try {
      const result = (await api('/admin/support-cases')) as { cases?: SupportCase[] };
      setCases(result.cases ?? []);
    } catch (value) {
      setError(value instanceof Error ? value.message : 'Unable to load support cases.');
    }
  }, [api]);
  useEffect(() => {
    void load();
  }, [load]);
  const create = async () => {
    try {
      setError('');
      await api('/admin/support-cases', { method: 'POST', body: JSON.stringify(draft) });
      setDraft({ targetUserId: '', subject: '', note: '', priority: 'normal' });
      await load();
    } catch (value) {
      setError(value instanceof Error ? value.message : 'Unable to create the support case.');
    }
  };
  const update = async (caseId: string, status: string, assignedAdminId?: string) => {
    const reason = window.prompt(`Reason for ${status}:`, '')?.trim();
    if (!reason) return;
    const resolution = ['resolved', 'closed'].includes(status)
      ? window.prompt('Resolution:', '')?.trim()
      : '';
    if (['resolved', 'closed'].includes(status) && !resolution) return;
    try {
      setError('');
      await api('/admin/support-cases', {
        method: 'PATCH',
        body: JSON.stringify({ caseId, status, reason, resolution, assignedAdminId }),
      });
      await load();
    } catch (value) {
      setError(value instanceof Error ? value.message : 'Unable to update the support case.');
    }
  };
  return (
    <div className="workspace">
      {error && <div className="alert error">{error}</div>}
      {writable && (
        <section className="panel">
          <div className="panel-heading">
            <h2>Create support case</h2>
          </div>
          <div className="expense-form">
            <input
              placeholder="Account ID"
              value={draft.targetUserId}
              onChange={(event) => setDraft({ ...draft, targetUserId: event.target.value })}
            />
            <input
              placeholder="Subject"
              value={draft.subject}
              onChange={(event) => setDraft({ ...draft, subject: event.target.value })}
            />
            <select
              value={draft.priority}
              onChange={(event) => setDraft({ ...draft, priority: event.target.value })}
            >
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
            <textarea
              placeholder="Internal note"
              value={draft.note}
              onChange={(event) => setDraft({ ...draft, note: event.target.value })}
            />
            <button
              disabled={!draft.targetUserId || !draft.subject || !draft.note}
              onClick={() => void create()}
            >
              Create case
            </button>
          </div>
        </section>
      )}
      <section className="panel">
        <div className="panel-heading">
          <h2>Support cases</h2>
          <button className="secondary" onClick={() => void load()}>
            Refresh
          </button>
        </div>
        <div className="data-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Updated</th>
                <th>Account</th>
                <th>Subject</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Assigned</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {cases.map((item) => (
                <tr key={item.caseId}>
                  <td>{asDate(item.updatedAt)}</td>
                  <td>
                    <code>{item.targetUserId}</code>
                  </td>
                  <td>
                    {item.subject}
                    <small>{item.note}</small>
                  </td>
                  <td>{label(item.priority)}</td>
                  <td>
                    {label(item.status)}
                    <small>{item.resolution}</small>
                  </td>
                  <td>{item.assignedAdminEmail || item.assignedAdminId}</td>
                  <td>
                    {writable && (
                      <div className="card-actions">
                        <button
                          className="secondary"
                          onClick={() => {
                            const assignee = window
                              .prompt('Delegated administrator ID:', '')
                              ?.trim();
                            if (assignee) void update(item.caseId, item.status, assignee);
                          }}
                        >
                          Assign
                        </button>
                        <button
                          className="secondary"
                          onClick={() => void update(item.caseId, 'in_progress')}
                        >
                          Start
                        </button>
                        <button onClick={() => void update(item.caseId, 'resolved')}>
                          Resolve
                        </button>
                        <button
                          className="secondary"
                          onClick={() => void update(item.caseId, 'closed')}
                        >
                          Close
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

export function ContentOperationsPanel({ api, writable }: { api: AdminApi; writable: boolean }) {
  const [data, setData] = useState<ContentOperationsData>();
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    try {
      setData((await api('/admin/content-operations')) as ContentOperationsData);
    } catch (value) {
      setError(value instanceof Error ? value.message : 'Unable to load content operations.');
    }
  }, [api]);
  useEffect(() => {
    void load();
  }, [load]);
  const act = async (resourceType: string, resourceId: string, action: string, type?: string) => {
    const reason = window.prompt(`Review reason for ${action}:`, '')?.trim();
    if (!reason) return;
    await api('/admin/content-operations/action', {
      method: 'PATCH',
      body: JSON.stringify({ resourceType, resourceId, action, type, reason }),
    });
    await load();
  };
  if (error) return <div className="alert error">{error}</div>;
  if (!data) return null;
  return (
    <>
      <section className="panel">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">Mobile cache control</p>
            <h2>Published content versions</h2>
          </div>
        </div>
        <div className="data-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Namespace</th>
                <th>Version</th>
                <th>Published</th>
                <th>Source</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(data.contentManifest?.namespaces ?? {}).map(
                ([namespace, version]) => (
                  <tr key={namespace}>
                    <td>{label(namespace)}</td>
                    <td>{version.version}</td>
                    <td>{asDate(version.updatedAt)}</td>
                    <td>{version.updatedBy || 'Initial version'}</td>
                    <td>
                      {writable && (
                        <div className="card-actions">
                          <button
                            onClick={() =>
                              void act('content_namespace', namespace, 'publish')
                            }
                          >
                            Publish version
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      </section>
      <section className="panel">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">Publishing workflow</p>
            <h2>Reading passages</h2>
          </div>
        </div>
        <div className="data-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Level</th>
                <th>Copyright</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(data.readingPassages ?? []).map((passage) => (
                <tr key={passage.passageId}>
                  <td>{passage.title}</td>
                  <td>{label(passage.level)}</td>
                  <td>
                    {label(passage.copyrightBasis)}
                    <small>{passage.licenseReference}</small>
                  </td>
                  <td>{label(passage.status)}</td>
                  <td>
                    {writable && (
                      <div className="card-actions">
                        <button
                          disabled={!passage.safetyReviewedAt}
                          onClick={() => void act('reading_passage', passage.passageId, 'publish')}
                        >
                          Publish
                        </button>
                        {passage.status === 'safety_review' ? (
                          <button
                            className="secondary"
                            onClick={() =>
                              void act('reading_passage', passage.passageId, 'approve_safety')
                            }
                          >
                            Approve safety
                          </button>
                        ) : (
                          <button
                            className="secondary"
                            onClick={() =>
                              void act(
                                'reading_passage',
                                passage.passageId,
                                'request_safety_review'
                              )
                            }
                          >
                            Request safety review
                          </button>
                        )}
                        <button
                          className="secondary"
                          onClick={() => void act('reading_passage', passage.passageId, 'archive')}
                        >
                          Archive
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <section className="panel">
        <div className="panel-heading">
          <h2>AI clarifications</h2>
        </div>
        <div className="data-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Question</th>
                <th>Type</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(data.clarifications ?? []).map((item) => (
                <tr key={`${item.id}:${item.type}`}>
                  <td>{item.question || item.questionText || item.id}</td>
                  <td>{item.type}</td>
                  <td>{label(item.status)}</td>
                  <td>
                    {writable && (
                      <div className="card-actions">
                        <button
                          onClick={() => void act('clarification', item.id, 'approve', item.type)}
                        >
                          Approve
                        </button>
                        <button
                          className="danger"
                          onClick={() => void act('clarification', item.id, 'reject', item.type)}
                        >
                          Reject
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <section className="panel">
        <div className="panel-heading">
          <h2>Generation jobs</h2>
          <span className="status-pill">Exam approval: {label(data.examContent?.status)}</span>
        </div>
        <div className="data-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Created</th>
                <th>Job</th>
                <th>Type</th>
                <th>Status</th>
                <th>Results</th>
              </tr>
            </thead>
            <tbody>
              {(data.generationJobs ?? []).map((job) => (
                <tr key={job.jobId}>
                  <td>{asDate(job.createdAt)}</td>
                  <td>
                    <code>{job.jobId}</code>
                  </td>
                  <td>{label(job.type)}</td>
                  <td>{label(job.status)}</td>
                  <td>{JSON.stringify(job.resultCounts ?? {})}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
